import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { supabase } from '@/lib/supabase';

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:outline-none';
const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

/**
 * Self-service password change for a signed-in user.
 * Uses supabase.auth.updateUser and records a 'password_changed' activity row.
 */
const ChangePasswordCard: React.FC<{ userId?: string | null }> = ({ userId }) => {
  const { t } = useI18n();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setSaved(false);
    if (pw.length < 6) return setError(t('auth.errPassword'));
    if (pw !== pw2) return setError(t('auth.errPasswordMatch'));

    setBusy(true);
    const { data, error: err } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (err) return setError(err.message);

    const id = data?.user?.id || userId;
    if (id) {
      await supabase.from('mkt_activity').insert({
        user_id: id,
        action: 'password_changed',
        detail: 'profile-settings',
      });
    }
    setPw('');
    setPw2('');
    setSaved(true);
    window.setTimeout(() => setSaved(false), 6000);
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
        <KeyRound className="h-5 w-5 text-orange-500" /> {t('auth.changePassword')}
      </h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{t('auth.newPassword')}</label>
          <input
            type="password"
            autoComplete="new-password"
            className={inputCls}
            value={pw}
            onChange={(e) => { setPw(e.target.value); setError(''); setSaved(false); }}
          />
        </div>
        <div>
          <label className={labelCls}>{t('auth.confirmPassword')}</label>
          <input
            type="password"
            autoComplete="new-password"
            className={inputCls}
            value={pw2}
            onChange={(e) => { setPw2(e.target.value); setError(''); setSaved(false); }}
          />
        </div>
      </div>
      {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={busy || !pw || !pw2}
          className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? t('common.loading') : t('auth.setNewCta')}
        </button>
        {saved && <span className="text-sm font-semibold text-emerald-700">{t('auth.passwordSaved')}</span>}
      </div>
    </section>
  );
};

export default ChangePasswordCard;
